import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, CheckCircle2 } from "lucide-react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Slot negotiation for a guidance request. Both sides (client & mentor) see:
 *   • proposed_slots  (client's picks)
 *   • counter_slots   (mentor's counter-proposals)
 *   • agreed_slot     (once locked, payment unlocks)
 *
 * Either party can accept any pending slot or add a new counter. Backend
 * mirrors the identity check so we don't duplicate it here.
 */
export default function SlotNegotiator({ guidance, viewerRole, onChanged }) {
  const [newSlots, setNewSlots] = useState([""]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [mentorWindows, setMentorWindows] = useState([]);

  useEffect(() => {
    // Show the mentor's recurring windows as a hint so the client picks slots
    // inside them. Public endpoint, no auth needed.
    if (!guidance?.mentor_id) return;
    api.get(`/mentors/${guidance.mentor_id}`).then((r) => setMentorWindows(r.data?.availability_windows || [])).catch(() => {});
  }, [guidance?.mentor_id]);

  const proposed = guidance.proposed_slots || [];
  const counter = guidance.counter_slots || [];
  const combined = [
    ...proposed.map((s) => ({ slot: s, by: "client" })),
    ...counter.map((s) => ({ slot: s, by: "mentor" })),
  ];
  const agreed = guidance.agreed_slot || null;

  const accept = async (slot) => {
    setBusy(true);
    try { await api.post(`/guidance/${guidance.id}/accept-slot`, { slot }); toast.success("Slot locked — payment unlocks"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const propose = async () => {
    const filtered = newSlots.filter((s) => (s || "").trim());
    if (filtered.length === 0) { toast.error("Add at least one slot"); return; }
    setBusy(true);
    try {
      await api.post(`/guidance/${guidance.id}/propose-slots`, { slots: filtered, note });
      setNewSlots([""]); setNote(""); toast.success("Slots posted");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (agreed) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 rounded-sm px-4 py-3 text-sm text-emerald-900" data-testid="slot-agreed">
        <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
        Meeting locked for <strong>{new Date(agreed).toLocaleString()}</strong>. Payment gate unlocks next.
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white" data-testid="slot-negotiator">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2 inline-flex items-center gap-1">
        <Calendar className="w-3 h-3" /> SCHEDULE NEGOTIATION
      </div>
      {combined.length === 0 ? (
        <div className="text-xs text-kb-ink-muted">No slots proposed yet — {viewerRole === "client" ? "add your preferred times below" : "waiting for the client to propose slots"}.</div>
      ) : (
        <ul className="space-y-1 mb-3">
          {combined.map((s, i) => (
            <li key={i} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-sm px-3 py-1.5">
              <span className="flex-1">{new Date(s.slot).toLocaleString()}</span>
              <span className={`kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${s.by === "client" ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{s.by}</span>
              {/* Only the OTHER party can accept a proposal. */}
              {((s.by === "client" && viewerRole !== "client") || (s.by === "mentor" && viewerRole === "client")) && (
                <button onClick={() => accept(s.slot)} disabled={busy} className="kb-btn-primary text-[10px] px-2 py-0.5" data-testid={`slot-accept-${i}`}>Accept</button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-slate-100 pt-3 space-y-2">
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
          {viewerRole === "client" ? "Propose slots" : "Counter-propose"}
        </div>
        {mentorWindows.length > 0 && (
          <div className="kb-mono text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1">
            Mentor prefers: {mentorWindows.map((w) => `${DAY_LABELS[w.day]} ${w.start}-${w.end}`).join(" · ")}
          </div>
        )}
        {newSlots.map((s, i) => (
          <div key={i} className="flex gap-2">
            <input type="datetime-local" className="kb-input flex-1" value={s}
              onChange={(e) => setNewSlots(newSlots.map((x, j) => j === i ? e.target.value : x))}
              data-testid={`slot-input-${i}`} />
            {newSlots.length > 1 && (
              <button type="button" onClick={() => setNewSlots(newSlots.filter((_, j) => j !== i))} className="kb-btn-outline text-xs px-2">✕</button>
            )}
          </div>
        ))}
        <button type="button" onClick={() => setNewSlots([...newSlots, ""])} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline">
          + Add another slot
        </button>
        <input type="text" className="kb-input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        <button onClick={propose} disabled={busy} className="kb-btn-primary text-xs" data-testid="slot-propose-btn">
          {busy ? "Posting…" : (viewerRole === "client" ? "Propose slots" : "Counter-propose")}
        </button>
      </div>
    </div>
  );
}
